<template>
  <section class="max-w-4xl mx-auto px-6 py-16 text-gray-800">
<div class="text-center mb-12">
  <!-- Linie oben -->
  <div class="w-48 h-[3px] mx-auto mb-4 rounded-full bg-gradient-to-r from-blue-100 via-green-100 to-blue-200"></div>

  <!-- Überschrift -->
  <h1 
    ref="headingRef"
    class="text-4xl font-semibold leading-snug tracking-tight text-gray-800"
  >
    Therapeutic Approach
  </h1>

  <!-- Linie unten -->
  <div class="w-48 h-[3px] mx-auto mt-4 rounded-full bg-gradient-to-r from-blue-200 via-green-100 to-blue-100"></div>
</div>





    <!-- Section: Overview -->
    <div ref="overviewRef" class="mb-16 text-center">
      <p class="text-lg leading-relaxed">
        As a counselling psychologist, my therapeutic approach is integrative and client-centred,
        grounded in both Cognitive Behavioural Therapy (CBT) and psychodynamic principles. I draw
        on these models flexibly, depending on your needs, concerns, and goals.
      </p>
    </div>

    <!-- Section: CBT -->
    <div ref="cbtRef" class="mb-16">
      <h2 class="text-2xl font-bold mb-4 flex items-center gap-2"><span>🧠</span> CBT – Practical Tools for Change</h2>
      <p class="mb-4">
        CBT techniques help clients identify unhelpful thinking patterns and develop more adaptive
        strategies. It offers practical tools that can be applied outside the therapy room.
      </p>
      <ul class="list-disc list-inside space-y-2 pl-4">
        <li><strong>Collaborative formulation:</strong> Understanding how thoughts, behaviours, and emotions interact.</li>
        <li><strong>Cognitive restructuring:</strong> Reframing distorted thinking in a balanced way.</li>
        <li><strong>Behavioural strategies:</strong> Building confidence through exposure and activity planning.</li>
        <li><strong>Psychoeducation:</strong> Empowering clients through knowledge.</li>
      </ul>
    </div>

    <!-- Section: Psychodynamic -->
    <div ref="psychodynamicRef" class="mb-16">
      <h2 class="text-2xl font-bold mb-4 flex items-center gap-2"><span>🌊</span> Psychodynamic Therapy – Understanding the Deeper Story</h2>
      <p class="mb-4">
        This approach helps clients explore long-standing patterns, emotional experiences, and unconscious processes influencing current struggles.
      </p>
      <ul class="list-disc list-inside space-y-2 pl-4">
        <li><strong>Unconscious processes:</strong> Becoming aware of hidden drivers of behaviour.</li>
        <li><strong>Therapeutic relationship:</strong> Using the client–therapist dynamic for insight.</li>
        <li><strong>Defences and resistance:</strong> Exploring difficult emotions in a safe space.</li>
        <li><strong>Emotional expression:</strong> Encouraging reflection and healing through connection.</li>
      </ul>
    </div>

    <!-- Section: Style -->
    <div ref="styleRef">
      <h2 class="text-2xl font-bold mb-4 flex items-center gap-2"><span>🎯</span> My Therapeutic Style</h2>
      <ul class="list-disc list-inside space-y-2 pl-4">
        <li>Warm, collaborative, and respectful in all interactions.</li>
        <li>Curiosity and flexibility guide the therapeutic process.</li>
        <li>Combining CBT's practicality with psychodynamic depth.</li>
      </ul>
      <p class="mt-4">
        I see therapy as a shared journey. My role is to walk alongside you—offering both tools and reflection—supporting deeper connection with yourself and others.
      </p>
    </div>
  </section>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const headingRef = ref(null)
const overviewRef = ref(null)
const cbtRef = ref(null)
const psychodynamicRef = ref(null)
const styleRef = ref(null)

onMounted(() => {
  const animate = (el, x = 0, y = 40) => {
    if (el) {
      gsap.from(el, {
        opacity: 0,
        x,
        y,
        duration: 1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
        }
      })
    }
  }

  animate(headingRef.value, 0, 20)
  animate(overviewRef.value, -50, 0)
  animate(cbtRef.value, 50, 0)
  animate(psychodynamicRef.value, -50, 0)
  animate(styleRef.value, 0, 30)
})
</script>
